import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Portal-based multi-select dropdown component to solve z-index issues
const PortalMultiSelectDropdown = ({ options, selected, onChange, placeholder, displayKey, searchKey }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const filteredOptions = options.filter(option => 
    option[searchKey || displayKey].toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    const isSelected = selected.includes(option._id);
    if (isSelected) {
      onChange(selected.filter(id => id !== option._id));
    } else {
      onChange([...selected, option._id]);
    }
  };

  const handleRemove = (optionId) => {
    onChange(selected.filter(id => id !== optionId));
  };

  const getSelectedOptions = () => {
    return options.filter(option => selected.includes(option._id));
  };
  // Calculate dropdown position
  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;

      setDropdownPosition({
        top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Open dropdown and calculate position
  const openDropdown = () => {
    updatePosition();
    setIsOpen(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  return (
    <>
      <div ref={triggerRef} className="relative">
        <div 
          className="input input-bordered min-h-12 cursor-pointer flex flex-wrap gap-1 items-center p-2 transition-all duration-300 bg-white/80 backdrop-blur-sm"
          onClick={openDropdown}
        >
          {getSelectedOptions().length > 0 ? (
            getSelectedOptions().map(option => (
              <div key={option._id} className="badge badge-primary gap-1" style={{background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)'}}>
                {option[displayKey]}
                <button
                  type="button"
                  className="btn btn-ghost btn-xs text-white hover:bg-primary-focus transition-all duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(option._id);
                  }}
                >
                  ✕
                </button>
              </div>
            ))
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
          <div className="ml-auto">
            <svg className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Portal-rendered dropdown */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="bg-white border-2 border-gray-200 rounded-lg shadow-2xl max-h-60 overflow-hidden"
          style={{ 
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 999999
          }}
        >
          <div className="p-2 border-b border-gray-200 bg-white">
            <input
              type="text"
              className="input input-sm input-bordered w-full bg-white"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto bg-white">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div
                  key={option._id}
                  className={`p-3 cursor-pointer hover:bg-blue-50 flex items-center gap-2 transition-all duration-200 ${
                    selected.includes(option._id) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={selected.includes(option._id)}
                    readOnly
                  />
                  <span className="flex-1">{option[displayKey]}</span>
                </div>
              ))
            ) : (
              <div className="p-3 text-gray-500 text-center">No teams found</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PortalMultiSelectDropdown;
